import React, { FC } from 'react';

import { TEXTS } from 'application/pages/BillingPage/renderer/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { useNavigate } from 'application/providers/RouterProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Info from '@zvuk-b2b/react-uikit/ui/Info';

import { LicensesList, LicensesUnpaidList, ChangeAutoRenewalButton } from './components';
import useLicensesModalViewModel from './useLicensesModalViewModel';

import styles from './index.module.scss';

const LicensesModal: FC<ModalProps[ModalTypes.LICENSES]> = ({
  openModal,
  close,
  payFrequencyMonth = 1,
  unpaidLicensesOnly = false,
  isViewMode = false,
}) => {
  const notification = useNotificationContext();
  const navigate = useNavigate();

  const viewModel = useLicensesModalViewModel({
    notification,
    unpaidLicensesOnly,
    isViewMode,
    openModal,
    close,
    navigate,
    closeModalButton: (
      <Button
        key="button_confirm_autorenewal"
        elementId="button_confirm_autorenewal"
        onClick={() => close(2)}>
        {TEXTS.BILLING_AUTORENEWAL_SUSPENDED_SUBMIT}
      </Button>
    ),
  });

  if (!viewModel) {
    return (
      <ModalScreen
        title={TEXTS.BILLING_LICENSES_MODAL_TITLE}
        titlePlacement="left"
        onClose={close}
        containerClassName={styles.licensesModalContainer}
        contentClassName={styles.licensesModalContent}
        containerActionButtonsClassName={styles.licensesModalActionButtons}>
        <div className={styles.licensesSkeleton} />
      </ModalScreen>
    );
  }

  const {
    viewMode,
    toSuspendAutorenewal,
    toEnableAutorenewal,
    isChangeAutoRenewalButtonDisabled,
    totalLicenses,
    unpaidLicenses,
    licensesSubscriptionCost,
    hasInactiveLicenses,
    isSubmitLoading,
    isNeedPayment,
    bindCardVisible,
    isLinkingInProcess,
    isHintShow,
    setEditMode,
    setSubscriptionCost,
    handleBindCard,
    handleChangeAutoRenewal,
    dispatch,
    handleLicensesModalClose,
  } = viewModel;

  const getActionButtons = () => {
    const buttons: React.ReactNode[] = [];

    if (viewMode) {
      buttons.push(
        <Button
          key="button_view_mode_ok"
          elementId="button_view_mode_ok"
          type={ButtonType.PRIMARY}
          onClick={() => close()}>
          {TEXTS.BILLING_LICENSES_OK}
        </Button>
      );

      buttons.push(
        <Button
          key="button_view_mode_set_edit"
          elementId="button_view_mode_set_edit"
          type={ButtonType.SECONDARY}
          onClick={setEditMode}>
          {TEXTS.BILLING_LICENSES_SET_EDIT_MODE}
        </Button>
      );
    } else {
      if (bindCardVisible) {
        buttons.push(
          <Button
            elementId="button_bind_card"
            type={ButtonType.PRIMARY}
            isLoading={isSubmitLoading}
            onClick={handleBindCard}>
            {TEXTS.BILLING_BIND_CARD_BUTTON_TEXT}
          </Button>
        );
      }

      buttons.push(
        <ChangeAutoRenewalButton
          key="button_submit_licenses"
          isDisabled={isChangeAutoRenewalButtonDisabled}
          isLoading={isSubmitLoading}
          isNeedPayment={isNeedPayment}
          licensesSubscriptionCost={licensesSubscriptionCost}
          isLinkingInProcess={isLinkingInProcess}
          isHintShow={isHintShow}
          onClick={handleChangeAutoRenewal}
        />
      );
    }

    return buttons;
  };

  return (
    <ModalScreen
      title={
        hasInactiveLicenses
          ? TEXTS.BILLING_LICENSES_RESUME_MODAL_TITLE(payFrequencyMonth === 1)
          : TEXTS.BILLING_LICENSES_MODAL_TITLE
      }
      titlePlacement="left"
      onClose={handleLicensesModalClose}
      containerClassName={styles.licensesModalContainer}
      contentClassName={styles.licensesModalContent}
      containerActionButtonsClassName={styles.licensesModalActionButtons}
      actionButtons={getActionButtons()}>
      {unpaidLicensesOnly ? (
        <LicensesUnpaidList
          unpaidLicenses={unpaidLicenses}
          toEnableAutorenewal={toEnableAutorenewal}
          dispatch={dispatch}
          setSubscriptionCost={setSubscriptionCost}
        />
      ) : (
        <LicensesList
          totalLicenses={totalLicenses}
          toSuspendAutorenewal={toSuspendAutorenewal}
          toEnableAutorenewal={toEnableAutorenewal}
          dispatch={dispatch}
          setSubscriptionCost={setSubscriptionCost}
          isAllLicensesDisabled={viewMode}
        />
      )}

      <Info className={styles.licensesModalHintInfo}>
        {TEXTS.BILLING_LICENSES_UNPAID_MODAL_INFO(payFrequencyMonth === 1)}
      </Info>
    </ModalScreen>
  );
};

export default React.memo(LicensesModal);
